import type { MeasureDef } from "../config/metrics";
import type { AxisFormatter } from "./charts/XmrChart";

export interface AxisFormatting {
  axisFormatter?: AxisFormatter;
  tableFormatter?: AxisFormatter;
  axisName?: string;
}

export function buildAxisFormatter(measure?: MeasureDef): AxisFormatting {
  if (!measure) {
    const formatter: AxisFormatter = value => formatCompact(value);
    return { axisFormatter: formatter, tableFormatter: formatter };
  }

  const decimals = extractDecimalCount(measure.format);

  if (measure.format?.includes("%")) {
    const formatter: AxisFormatter = value => `${(value * 100).toFixed(decimals)}%`;
    return {
      axisFormatter: formatter,
      tableFormatter: formatter,
      axisName: "%",
    };
  }

  if (measure.unit === "count") {
    const formatter: AxisFormatter = value => formatCompact(value);
    return { axisFormatter: formatter, tableFormatter: formatter };
  }

  if (measure.unit === "ms") {
    const formatter: AxisFormatter = value => `${value.toFixed(Math.max(decimals, 0))} ms`;
    return { axisFormatter: formatter, tableFormatter: formatter, axisName: "ms" };
  }

  if (measure.unit?.startsWith("EUR")) {
    const axisName = formatUnitName(measure.unit);
    const axisFormatter: AxisFormatter = value => `€${formatCompact(value, decimals)}`;
    const tableFormatter: AxisFormatter = value => `€${formatNumber(value, decimals)}`;
    return { axisFormatter, tableFormatter, axisName };
  }

  const formatter: AxisFormatter = value => formatCompact(value, decimals);
  return { axisFormatter: formatter, tableFormatter: formatter };
}

function extractDecimalCount(format?: string, fallback = 2): number {
  if (!format) return fallback;
  const match = format.match(/\.([0-9]+)/);
  if (match && match[1]) {
    return match[1].length;
  }
  return fallback;
}

export function formatCompact(value: number, decimals = 1): string {
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(value / 1e12).toFixed(decimals)}t`;
  if (abs >= 1e9) return `${(value / 1e9).toFixed(decimals)}b`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(decimals)}m`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(decimals)}k`;
  return value.toFixed(Math.max(decimals, 0));
}

function formatUnitName(unit: string): string {
  if (unit === "EUR") return "€";
  if (unit === "EUR/1kClicks") return "€ / 1k clicks";
  if (unit === "EUR/1kImpressions") return "€ / 1k impressions";
  return unit;
}

function formatNumber(value: number, decimals: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
